import { requestJson, resolveBackupUrl } from './cloudBackup';

export function normalizeApproval(approval) {
  if (!approval) return null;
  return {
    ...approval,
    recipientName: approval.recipientName || approval.clientName || '',
    recipientEmail: approval.recipientEmail || approval.clientEmail || '',
    version: approval.version || approval.documentVersion || '',
    approvalPdfUrl: approval.approvalPdfUrl || approval.pdfDriveUrl || '',
    receiptUrl: approval.receiptUrl || approval.receiptDriveUrl || '',
    driveFolderUrl: approval.driveFolderUrl || approval.requestFolderUrl || '',
    emailSendStatus: approval.emailSendStatus || approval.EMAIL_SEND_STATUS || '',
    emailitEmailId: approval.emailitEmailId || approval.EMAILIT_EMAIL_ID || '',
    emailitProviderStatus: approval.emailitProviderStatus || approval.EMAILIT_PROVIDER_STATUS || '',
    emailSendDurationMs: Number(approval.emailSendDurationMs || approval.EMAIL_SEND_DURATION_MS || 0),
    deliveryAttemptCount: Number(approval.deliveryAttemptCount || approval.DELIVERY_ATTEMPT_COUNT || 0),
    sendErrorCode: approval.sendErrorCode || approval.LAST_SEND_ERROR_CODE || '',
    sendErrorMessage: approval.sendErrorMessage || approval.LAST_SEND_ERROR_MESSAGE || '',
    sendErrorAt: approval.sendErrorAt || approval.LAST_SEND_ERROR_AT || '',
    approvedPdfFileId: approval.approvedPdfFileId || approval.APPROVED_PDF_FILE_ID || '',
    approvedPdfEmailStatus: approval.approvedPdfEmailStatus || approval.APPROVED_PDF_EMAIL_STATUS || '',
    approvedPdfEmailSentAt: approval.approvedPdfEmailSentAt || approval.APPROVED_PDF_EMAIL_SENT_AT || '',
    approvedPdfEmailProvider: approval.approvedPdfEmailProvider || approval.APPROVED_PDF_EMAIL_PROVIDER || '',
  };
}

export async function getBusinessProfile(settings, includeLogoData = false) {
  const url = resolveBackupUrl(settings);
  const actionUrl = `${url}?action=getBusinessProfile&includeLogoData=${includeLogoData}`;
  const data = await requestJson(actionUrl);
  if (!data || (data.success !== true && data.status !== 'ok')) {
    throw new Error(data?.error || data?.message || 'Failed to load business profile');
  }
  if (!data.profile || typeof data.profile !== 'object') {
    throw new Error('Invalid business profile response');
  }
  return data.profile;
}

export async function saveBusinessProfile(settings, profile) {
  const url = resolveBackupUrl(settings);
  const data = await requestJson(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({ action: 'saveBusinessProfile', profile }),
  });
  const isSuccess = data && (data.success === true || data.status === 'ok');
  if (!isSuccess || !data.profile) {
    throw new Error(data?.error || data?.message || 'Failed to save business profile');
  }
  return data.profile;
}

export async function uploadBusinessLogo(settings, dataUrl, fileName) {
  const url = resolveBackupUrl(settings);
  const data = await requestJson(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({ action: 'uploadBusinessLogo', dataUrl, fileName }),
  });
  const isSuccess = data && (data.success === true || data.status === 'ok' || data.saved === true);
  if (!isSuccess || !data.logo || !data.logo.fileId || !data.logo.fileName) {
    throw new Error(data?.error || data?.message || 'Failed to upload logo');
  }
  return {
    logoFileId: data.logo.fileId,
    logoFileName: data.logo.fileName,
    logoDataUrl: data.logo.dataUrl || '',
    mimeType: data.logo.mimeType,
    sizeBytes: data.logo.sizeBytes,
  };
}

export async function getDocumentConfig(settings) {
  const url = resolveBackupUrl(settings);
  const actionUrl = `${url}?action=getDocumentConfig`;
  const data = await requestJson(actionUrl);
  if (!data || (data.success !== true && data.status !== 'ok')) {
    throw new Error(data?.error || data?.message || 'Failed to load document config');
  }
  if (!data.documentConfig || typeof data.documentConfig !== 'object') {
    throw new Error('Invalid document config response');
  }
  return data.documentConfig;
}

export async function reserveDocumentNumber(settings, proposalId, prefix) {
  const url = resolveBackupUrl(settings);
  const data = await requestJson(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({ action: 'reserveDocumentNumber', proposalId, prefix }),
    timeoutMs: 30000,
  });
  const isSuccess = data && (data.success === true || data.status === 'ok' || data.reserved === true);
  if (!isSuccess || !data.documentNumber) {
    throw new Error(data?.error || data?.message || 'Failed to reserve document number');
  }
  return {
    documentNumber: data.documentNumber,
    prefix: data.prefix,
    year: data.year,
    sequence: data.sequence,
    existing: data.existing,
    documentNumberAssignedAt: new Date().toISOString(),
  };
}

export async function generateProfessionalPdf(settings, proposal, businessProfile) {
  const url = resolveBackupUrl(settings);
  const data = await requestJson(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({ action: 'generateProfessionalPdf', proposal, settings, profile: businessProfile }),
    timeoutMs: 90000,
  });
  const isSuccess = data && (data.success === true || data.status === 'ok' || data.generated === true);
  if (!isSuccess || !data.pdfBase64 || !data.fileName) {
    throw new Error(data?.error || data?.message || 'Failed to generate professional PDF');
  }
  return {
    pdfBase64: data.pdfBase64,
    fileName: data.fileName,
    documentNumber: data.documentNumber,
    generationId: data.generationId,
    pdfSizeBytes: data.pdfSizeBytes,
    googleDocFileId: data.googleDocFileId,
    pdfFileId: data.pdfFileId,
    googleDocUrl: data.googleDocUrl,
    pdfDriveUrl: data.pdfDriveUrl,
  };
}

export async function getApprovalConfig(settings) {
  const url = resolveBackupUrl(settings);
  const actionUrl = `${url}?action=getApprovalConfig`;
  const data = await requestJson(actionUrl);
  const isSuccess = data && (data.success === true || data.status === 'ok');
  if (!isSuccess) {
    throw new Error(data?.error || data?.message || 'Failed to load approval config');
  }
  if (!data.approvalConfig || typeof data.approvalConfig !== 'object') {
    throw new Error('Invalid approval config response');
  }
  return data.approvalConfig;
}

export async function sendForApproval(settings, payload) {
  const url = resolveBackupUrl(settings);
  const data = await requestJson(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({
      action: 'sendForApproval',
      proposal: payload.proposal,
      settings,
      recipientName: payload.recipientName,
      recipientEmail: payload.recipientEmail,
      recipientMobile: payload.recipientMobile,
      customMessage: payload.customMessage,
    }),
    timeoutMs: 150000,
  });
  const isSuccess = data && (data.success === true || data.status === 'ok' || data.sent === true);
  if (!isSuccess) {
    throw new Error(data?.error || data?.message || 'Failed to send proposal for approval');
  }
  let approval = data.approval;
  if (!approval && data.requestId && data.status) {
    approval = data;
  }
  if (!approval) {
    throw new Error('Server did not return a valid approval object');
  }
  return normalizeApproval(approval);
}

export async function getApprovalStatus(settings, { requestId, proposalId }) {
  const url = resolveBackupUrl(settings);
  const data = await requestJson(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({ action: 'getApprovalStatus', requestId, proposalId }),
    timeoutMs: 45000,
  });
  const isSuccess = data && (data.success === true || data.status === 'ok');
  if (!isSuccess) {
    throw new Error(data?.error || data?.message || 'Failed to get approval status');
  }
  if (data.approval === null) {
    return null;
  }
  if (data.approval === undefined) {
    throw new Error('Invalid approval status response');
  }
  return normalizeApproval(data.approval);
}

export async function getApprovalOverview(settings, {
  requestId,
  proposalId,
  documentNumber,
  documentVersion,
  historyLimit = 25
}) {
  const url = resolveBackupUrl(settings);
  const data = await requestJson(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({
      action: 'getApprovalOverview',
      requestId,
      proposalId,
      documentNumber,
      documentVersion,
      historyLimit,
    }),
    timeoutMs: 90000,
  });
  const isSuccess = data && (data.success === true || data.status === 'ok');
  if (!isSuccess) {
    throw new Error(data?.error || data?.message || 'Failed to get approval overview');
  }
  const overview = data.overview || data.data?.overview || {};
  return {
    proposalId: overview.proposalId || proposalId,
    requestedRequest: overview.requestedRequest ? normalizeApproval(overview.requestedRequest) : null,
    latestRequest: overview.latestRequest ? normalizeApproval(overview.latestRequest) : null,
    latestExactRequest: overview.latestExactRequest ? normalizeApproval(overview.latestExactRequest) : null,
    latestApproved: overview.latestApproved ? normalizeApproval(overview.latestApproved) : null,
    exactApproved: overview.exactApproved ? normalizeApproval(overview.exactApproved) : null,
    canRestoreApprovedVersion: overview.canRestoreApprovedVersion || false,
    openRequests: Array.isArray(overview.openRequests) ? overview.openRequests.map(normalizeApproval) : [],
    history: Array.isArray(overview.history) ? overview.history.map(normalizeApproval) : [],
  };
}

export async function restoreApprovedVersion(settings, {
  requestId,
  proposalId,
  documentNumber,
  documentVersion,
  revokeStalePending = true
}) {
  const url = resolveBackupUrl(settings);
  const data = await requestJson(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({
      action: 'restoreApprovedVersion',
      requestId,
      proposalId,
      documentNumber,
      documentVersion,
      revokeStalePending,
    }),
    timeoutMs: 60000,
  });
  const isSuccess = data && (data.success === true || data.status === 'ok' || data.restored === true);
  if (!isSuccess) {
    throw new Error(data?.error || data?.message || 'Failed to restore approved version');
  }
  const overview = data.overview || data.data?.overview || {};
  return {
    approval: data.approval ? normalizeApproval(data.approval) : null,
    overview: {
      proposalId: overview.proposalId || proposalId,
      requestedRequest: overview.requestedRequest ? normalizeApproval(overview.requestedRequest) : null,
      latestRequest: overview.latestRequest ? normalizeApproval(overview.latestRequest) : null,
      latestExactRequest: overview.latestExactRequest ? normalizeApproval(overview.latestExactRequest) : null,
      latestApproved: overview.latestApproved ? normalizeApproval(overview.latestApproved) : null,
      exactApproved: overview.exactApproved ? normalizeApproval(overview.exactApproved) : null,
      canRestoreApprovedVersion: overview.canRestoreApprovedVersion || false,
      openRequests: Array.isArray(overview.openRequests) ? overview.openRequests.map(normalizeApproval) : [],
      history: Array.isArray(overview.history) ? overview.history.map(normalizeApproval) : [],
    },
    revokedPendingCount: Number(data.revokedPendingCount || 0),
    originalDecisionPreserved: data.originalDecisionPreserved !== false,
  };
}

export async function recoverUnchangedApprovedVersion(settings, {
  proposalId,
  documentNumber,
  currentDocumentVersion,
  approvedRequestId,
  approvedDocumentVersion,
  ownerAttestation,
  revokeStalePending = true,
}) {
  const url = resolveBackupUrl(settings);
  const data = await requestJson(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({
      action: 'recoverUnchangedApprovedVersion',
      proposalId,
      documentNumber,
      currentDocumentVersion,
      approvedRequestId,
      approvedDocumentVersion,
      ownerAttestation,
      revokeStalePending,
    }),
    timeoutMs: 90000,
  });
  const isSuccess = data && (data.success === true || data.status === 'ok' || data.recovered === true);
  if (!isSuccess) {
    throw new Error(data?.error || data?.message || 'Failed to recover unchanged approved version');
  }
  const overview = data.overview || data.data?.overview || {};
  return {
    approval: data.approval ? normalizeApproval(data.approval) : null,
    overview: {
      proposalId: overview.proposalId || proposalId,
      requestedRequest: overview.requestedRequest ? normalizeApproval(overview.requestedRequest) : null,
      latestRequest: overview.latestRequest ? normalizeApproval(overview.latestRequest) : null,
      latestExactRequest: overview.latestExactRequest ? normalizeApproval(overview.latestExactRequest) : null,
      latestApproved: overview.latestApproved ? normalizeApproval(overview.latestApproved) : null,
      exactApproved: overview.exactApproved ? normalizeApproval(overview.exactApproved) : null,
      canRestoreApprovedVersion: overview.canRestoreApprovedVersion || false,
      openRequests: Array.isArray(overview.openRequests) ? overview.openRequests.map(normalizeApproval) : [],
      history: Array.isArray(overview.history) ? overview.history.map(normalizeApproval) : [],
    },
    recoveredFromVersion: data.recoveredFromVersion || currentDocumentVersion || '',
    recoveredToVersion: data.recoveredToVersion || approvedDocumentVersion || '',
    revokedPendingCount: Number(data.revokedPendingCount || 0),
    originalDecisionPreserved: data.originalDecisionPreserved !== false,
    ownerAttestationRecorded: data.ownerAttestationRecorded === true,
    auditRowsDeleted: Number(data.auditRowsDeleted || 0),
  };
}

export async function downloadApprovedSnapshot(settings, {
  proposalId,
  documentNumber,
  approvedRequestId,
  approvedDocumentVersion,
}) {
  const url = resolveBackupUrl(settings);
  const data = await requestJson(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({
      action: 'downloadApprovedSnapshot',
      proposalId,
      documentNumber,
      approvedRequestId,
      approvedDocumentVersion,
    }),
    timeoutMs: 180000,
  });
  const isSuccess = data && (data.success === true || data.status === 'ok');
  if (!isSuccess) {
    throw new Error(data?.error || data?.message || 'Failed to download approved snapshot');
  }
  return {
    requestId: data.requestId || approvedRequestId || '',
    proposalId: data.proposalId || proposalId || '',
    documentNumber: data.documentNumber || documentNumber || '',
    documentVersion: data.documentVersion || approvedDocumentVersion || '',
    decisionAt: data.decisionAt || '',
    fileName: data.fileName || '',
    mimeType: data.mimeType || 'application/pdf',
    pdfSizeBytes: Number(data.pdfSizeBytes || 0),
    pdfBase64: data.pdfBase64 || '',
  };
}

export async function emailApprovedPdfToClient(settings, {
  proposalId,
  requestId,
  documentNumber,
  documentVersion,
  fileName,
  pdfBase64,
  customMessage = ''
}) {
  const url = resolveBackupUrl(settings);
  const data = await requestJson(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({
      action: 'emailApprovedPdfToClient',
      proposalId,
      requestId,
      documentNumber,
      documentVersion,
      fileName,
      pdfBase64,
      customMessage
    }),
    timeoutMs: 240000,
  });

  const isSuccess = data && (data.success === true || data.status === 'ok' || data.emailed === true);
  if (!isSuccess) {
    throw new Error(data?.error || data?.message || 'Failed to email approved PDF to client');
  }

  // v5.2 returns data.approval — require it
  if (!data.approval) {
    throw new Error('Server did not return a valid approval object after emailing');
  }

  const normalized = normalizeApproval(data.approval);

  // Preserve safe top-level email fields by merging when needed
  if (data.recipientEmail && !normalized.recipientEmail) {
    normalized.recipientEmail = data.recipientEmail;
  }
  if (data.approvedPdfFileId && !normalized.approvedPdfFileId) {
    normalized.approvedPdfFileId = data.approvedPdfFileId;
  }
  if (data.approvedPdfDriveUrl && !normalized.approvalPdfUrl) {
    normalized.approvalPdfUrl = data.approvedPdfDriveUrl;
  }
  if (data.emailStatus && !normalized.approvedPdfEmailStatus) {
    normalized.approvedPdfEmailStatus = data.emailStatus;
  }
  if (data.emailSentAt && !normalized.approvedPdfEmailSentAt) {
    normalized.approvedPdfEmailSentAt = data.emailSentAt;
  }
  if (data.emailProvider && !normalized.approvedPdfEmailProvider) {
    normalized.approvedPdfEmailProvider = data.emailProvider;
  }
  if (data.emailId && !normalized.emailitEmailId) {
    normalized.emailitEmailId = data.emailId;
  }

  return normalized;
}

export async function resendApproval(settings, requestId) {
  const url = resolveBackupUrl(settings);
  const data = await requestJson(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({ action: 'resendApproval', requestId }),
    timeoutMs: 90000,
  });
  const isSuccess = data && (data.success === true || data.status === 'ok' || data.resent === true);
  if (!isSuccess) {
    throw new Error(data?.error || data?.message || 'Failed to resend approval');
  }
  let approval = data.approval;
  if (!approval && data.requestId && data.status) {
    approval = data;
  }
  if (!approval) {
    throw new Error('Server did not return a valid approval object');
  }
  return normalizeApproval(approval);
}

export async function revokeApproval(settings, requestId) {
  const url = resolveBackupUrl(settings);
  const data = await requestJson(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({ action: 'revokeApproval', requestId }),
  });
  const isSuccess = data && (data.success === true || data.status === 'ok' || data.revoked === true);
  if (!isSuccess) {
    throw new Error(data?.error || data?.message || 'Failed to revoke approval');
  }
  let approval = data.approval;
  if (!approval && data.requestId && data.status) {
    approval = data;
  }
  if (!approval) {
    throw new Error('Server did not return a valid approval object');
  }
  return normalizeApproval(approval);
}